import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  MapPin,
  Landmark,
  Building,
  Trees,
  Coffee,
  Sun,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { searchCategory } from "../../redux/reducers/Properties/PropertiesSlice";
import { setSearchData } from "../../redux/reducers/Search/SearchSlice";
import { useNavigate } from "react-router-dom";

interface LocationInputSimpleProps {
  value: string;
  setValue: (val: string) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  propertyTypes?: string[];
  priceRange?: [number, number];
  guestCount?: number;
  resetForm?: () => void;
  onSearchComplete?: () => void;
  initialLat?: number | null;
  initialLng?: number | null;
  initialLocationData?: any;
}

interface PlaceData {
  neighborhood: string;
  city: string;
  country: string;
  lat?: number;
  lng?: number;
}

const popularNeighborhoods: Array<PlaceData & { icon: JSX.Element }> = [
  {
    neighborhood: "Miraflores",
    city: "Lima",
    country: "Peru",
    lat: -12.1216,
    lng: -77.0296,
    icon: <Sun className="w-5 h-5 text-[#F56948]" />,
  },
  {
    neighborhood: "Barranco",
    city: "Lima",
    country: "Peru",
    lat: -12.1472,
    lng: -77.0207,
    icon: <Coffee className="w-5 h-5 text-[#F56948]" />,
  },
  {
    neighborhood: "San Isidro",
    city: "Lima",
    country: "Peru",
    lat: -12.1083,
    lng: -77.0373,
    icon: <Building className="w-5 h-5 text-[#F56948]" />,
  },
  {
    neighborhood: "Surco",
    city: "Lima",
    country: "Peru",
    lat: -12.113,
    lng: -76.9927,
    icon: <Trees className="w-5 h-5 text-[#F56948]" />,
  },
  {
    neighborhood: "La Molina",
    city: "Lima",
    country: "Peru",
    lat: -12.0905,
    lng: -76.9438,
    icon: <Landmark className="w-5 h-5 text-[#F56948]" />,
  },
  {
    neighborhood: "Pueblo Libre",
    city: "Lima",
    country: "Peru",
    lat: -12.0991,
    lng: -77.0507,
    icon: <MapPin className="w-5 h-5 text-[#F56948]" />,
  },
];

const LocationInputSimple: React.FC<LocationInputSimpleProps> = ({
  value,
  setValue,
  startDate,
  endDate,
  propertyTypes = [],
  priceRange = [0, 5000],
  guestCount = 1,
  resetForm,
  onSearchComplete,
  initialLat,
  initialLng,
  initialLocationData,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placeData, setPlaceData] = useState<PlaceData>({
    neighborhood: "",
    city: "",
    country: "",
  });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );

  const propertyTypesFromDB = useSelector(
    (state: RootState) => state.property.propertyTypes
  );

  useEffect(() => {
  console.log("🏠 Property types from DB:", propertyTypesFromDB);
}, [propertyTypesFromDB]);

  // ✅ Initialize placeData from Redux ONLY if placeData is empty
  useEffect(() => {
    if (
      initialLat && 
      initialLng && 
      initialLocationData && 
      !placeData.lat && 
      !placeData.lng
    ) {
      const locationData = {
        ...initialLocationData,
        lat: initialLat,
        lng: initialLng,
      };
      
      setPlaceData(locationData);
      
      const locationLabel = [
        locationData.neighborhood,
        locationData.city,
        locationData.country
      ]
        .filter(Boolean)
        .join(", ");
      
      if (!value) {
        setValue(locationLabel);
      }
      
      console.log("📍 LocationInputSimple - Loaded existing coordinates:", {
        lat: initialLat,
        lng: initialLng,
        locationData,
        displayValue: locationLabel,
      });
    }
  }, [initialLat, initialLng, initialLocationData]);

  // ✅ Inject custom styles for Google autocomplete dropdown
  useEffect(() => {
    const styleId = "google-autocomplete-custom-styles";

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        .pac-container {
          background-color: white;
          border-radius: 16px !important;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15) !important;
          border: none !important;
          margin-top: 8px !important;
          font-family: inherit !important;
          padding: 8px !important;
        }
        .pac-container::after {
          display: none !important;
        }
        .pac-item {
          padding: 12px 16px !important;
          border: none !important;
          border-radius: 12px !important;
          cursor: pointer !important;
          line-height: 1.5 !important;
          font-size: 14px !important;
          color: #374151 !important;
          transition: background-color 0.2s ease !important;
          margin: 2px 0 !important;
        }
        .pac-item:hover {
          background-color: #f9fafb !important;
        }
        .pac-item-selected {
          background-color: #f3f4f6 !important;
        }
        .pac-icon {
          margin-top: 4px !important;
          background-image: none !important;
          width: 20px !important;
          height: 20px !important;
        }
        .pac-icon::before {
          content: "📍";
          font-size: 16px;
          display: block;
        }
        .pac-item-query {
          font-size: 14px !important;
          font-weight: 500 !important;
          color: #111827 !important;
          padding-right: 8px !important;
        }
        .pac-item span:not(.pac-item-query) {
          font-size: 13px !important;
          color: #6b7280 !important;
        }
        .pac-matched {
          font-weight: 600 !important;
          color: #F56948 !important;
        }
        .pac-logo::after {
          padding: 8px 16px !important;
          margin: 4px 0 0 0 !important;
        }
        .hdpi.pac-logo::after {
          background-size: 120px 14px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ✅ Initialize Google Autocomplete with Place Details API
  useEffect(() => {
    let autocomplete: google.maps.places.Autocomplete | null = null;
    let placesService: google.maps.places.PlacesService | null = null;
    let interval: NodeJS.Timeout | null = null;

    const extractLocation = (
      place: google.maps.places.PlaceResult
    ): PlaceData => {
      console.log("🔍 Extracting from Place Details:", place);
      
      let neighborhood = "";
      let city = "";
      let country = "";
      let lat: number | undefined;
      let lng: number | undefined;

      // ✅ Extract coordinates
      if (place.geometry?.location) {
        lat = place.geometry.location.lat();
        lng = place.geometry.location.lng();
        console.log("✅ Extracted coordinates:", { lat, lng });
      }

      if (place.address_components) {
        // ✅ STRATEGY: Look for most specific location first
        for (const comp of place.address_components) {
          // Priority 1: Explicit neighborhood
          if (
            !neighborhood &&
            (comp.types.includes("neighborhood") ||
            comp.types.includes("sublocality_level_1") ||
            comp.types.includes("sublocality"))
          ) {
            neighborhood = comp.long_name;
            console.log("✅ Found neighborhood:", neighborhood);
          }
          
          // Priority 2: Locality (can be neighborhood or city depending on context)
          if (!neighborhood && comp.types.includes("locality")) {
            // If this is a district/neighborhood-level locality, use it as neighborhood
            if (comp.types.includes("political") && place.types?.includes("administrative_area_level_3")) {
              neighborhood = comp.long_name;
              console.log("✅ Using locality as neighborhood:", neighborhood);
            } else if (!city) {
              city = comp.long_name;
              console.log("✅ Using locality as city:", city);
            }
          }
          
          // Priority 3: Administrative area level 3 (often neighborhoods in Peru)
          if (!neighborhood && comp.types.includes("administrative_area_level_3")) {
            neighborhood = comp.long_name;
            console.log("✅ Using admin_area_level_3 as neighborhood:", neighborhood);
          }
          
          // City: admin_area_level_2 or locality
          if (!city && comp.types.includes("administrative_area_level_2")) {
            city = comp.long_name;
            console.log("✅ Found city (admin_area_level_2):", city);
          }
          
          // Country
          if (comp.types.includes("country")) {
            country = comp.long_name;
            console.log("✅ Found country:", country);
          }
        }
        
        // ✅ Fallback: Use place name if it's specific enough
        if (!neighborhood && place.name && place.types) {
          const isSpecificPlace = place.types.some(type => 
            ["neighborhood", "sublocality", "administrative_area_level_3", "locality"].includes(type)
          );
          if (isSpecificPlace) {
            neighborhood = place.name;
            console.log("✅ Using place name as neighborhood:", neighborhood);
          }
        }
      }
      
      // ✅ Default country to Peru if not found
      if (!country) {
        country = "Peru";
      }

      const result = { neighborhood, city, country, lat, lng };
      console.log("📍 FINAL Extracted location:", result);

      return result;
    };

    const initAutocomplete = () => {
      if (
        typeof window.google !== "undefined" &&
        window.google?.maps?.places?.Autocomplete &&
        inputRef.current &&
        !autocomplete
      ) {
        try {
          // ✅ Create autocomplete with minimal fields first
          autocomplete = new window.google.maps.places.Autocomplete(
            inputRef.current,
            {
              componentRestrictions: { country: "pe" },
              types: ["(regions)"], // ✅ Only regions (neighborhoods, cities, districts)
              fields: ["place_id", "name"], // ✅ Request only place_id initially
            }
          );

          // ✅ Create PlacesService for detailed queries
          const mapDiv = document.createElement('div');
          const map = new google.maps.Map(mapDiv);
          placesService = new google.maps.places.PlacesService(map);

          autocomplete.addListener("place_changed", () => {
            const place = autocomplete!.getPlace();
            
            if (!place || !place.place_id) {
              console.error("❌ No place_id returned from autocomplete");
              return;
            }

            console.log("🎯 Place selected, fetching details for place_id:", place.place_id);

            // ✅ Fetch complete place details using Place Details API
            placesService!.getDetails(
              {
                placeId: place.place_id,
                fields: [
                  "address_components",
                  "geometry",
                  "name",
                  "formatted_address",
                  "types",
                ],
              },
              (detailedPlace, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && detailedPlace) {
                  console.log("✅ Place Details received:", detailedPlace);
                  
                  const parsed = extractLocation(detailedPlace);
                  
                  // ✅ Validate we got coordinates
                  if (!parsed.lat || !parsed.lng) {
                    console.error("❌ No coordinates in place details");
                    return;
                  }
                  
                  // ✅ CHECK: Does this match a popular neighborhood? (more accurate coords)
                  const matchingNeighborhood = popularNeighborhoods.find(
                    (n) => n.neighborhood.toLowerCase() === parsed.neighborhood?.toLowerCase()
                  );
                  
                  if (matchingNeighborhood) {
                    console.log("🎯 Matched popular neighborhood! Using curated data:", matchingNeighborhood);
                    setPlaceData(matchingNeighborhood);
                    setValue(`${matchingNeighborhood.neighborhood}, ${matchingNeighborhood.city}, ${matchingNeighborhood.country}`);
                  } else {
                    // ✅ Use Place Details data
                    console.log("✅ Using Place Details data:", parsed);
                    setPlaceData(parsed);
                    setValue(detailedPlace.formatted_address || detailedPlace.name || "");
                  }
                } else {
                  console.error("❌ Place Details request failed:", status);
                }
              }
            );
          });

          console.log("✅ Google Autocomplete with Place Details initialized");
        } catch (error) {
          console.error("❌ Error initializing autocomplete:", error);
        }
      }
    };

    if (
      typeof window.google !== "undefined" &&
      window.google?.maps?.places?.Autocomplete
    ) {
      initAutocomplete();
    } else {
      interval = setInterval(() => {
        if (
          typeof window.google !== "undefined" &&
          window.google?.maps?.places?.Autocomplete
        ) {
          clearInterval(interval!);
          interval = null;
          initAutocomplete();
        }
      }, 300);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [setValue]);

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Convert property type names to IDs array
  const getPropertyTypeIds = (typeNames: string[]): string[] => {
    if (!propertyTypesFromDB || propertyTypesFromDB.length === 0) {
      console.warn("⚠️ Property types not loaded from database yet");
      return [];
    }

    const ids = typeNames
      .map((name) => {
        const propertyType = propertyTypesFromDB.find(
          (pt: any) => pt.name.toLowerCase() === name.toLowerCase()
        );
        return propertyType ? String(propertyType.id) : null;
      })
      .filter(Boolean) as string[];

    console.log("🔄 Converting property types to IDs:", {
      typeNames,
      ids,
    });

    return ids;
  };

  const handleSearch = async () => {
    let location: PlaceData;

    console.log("🔍 HANDLE SEARCH - Current placeData:", placeData);

    // ✅ Use current placeData
    if (placeData?.lat && placeData?.lng) {
      location = { ...placeData };
      console.log("📍 Using placeData:", location);
      
      // ✅ IF NEIGHBORHOOD IS STILL MISSING, DO REVERSE GEOCODE
      if (!location.neighborhood || location.neighborhood.trim() === "") {
        console.log("🔄 Neighborhood missing, attempting reverse geocode...");
        
        try {
          const reverseResponse = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&result_type=neighborhood|locality|sublocality&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
          );
          const reverseData = await reverseResponse.json();
          
          console.log("🔄 Reverse geocode response:", reverseData);
          
          if (reverseData.status === "OK" && reverseData.results?.length > 0) {
            for (const result of reverseData.results) {
              const components = result.address_components;
              
              for (const component of components) {
                if (
                  component.types.includes("neighborhood") ||
                  component.types.includes("sublocality_level_1") ||
                  component.types.includes("sublocality") ||
                  component.types.includes("locality")
                ) {
                  location.neighborhood = component.long_name;
                  console.log("✅ Reverse geocode found neighborhood:", location.neighborhood);
                  
                  if (component.types.includes("locality")) {
                    const adminComp = components.find((c: any) => 
                      c.types.includes("administrative_area_level_2")
                    );
                    if (adminComp && (!location.city || location.city === location.neighborhood)) {
                      location.city = adminComp.long_name;
                      console.log("✅ Updated city:", location.city);
                    }
                  }
                  break;
                }
              }
              if (location.neighborhood) break;
            }
          }
        } catch (err) {
          console.error("❌ Reverse geocode failed:", err);
        }
      }
    } else if (initialLat && initialLng && initialLocationData) {
      location = { 
        ...initialLocationData, 
        lat: initialLat, 
        lng: initialLng 
      };
      console.log("📍 Using initial location data:", location);
    } else {
      console.warn("⚠️ No coordinates available");
      alert("Please select a location from the suggestions");
      return;
    }

    // ✅ Validate coordinates
    if (!location.lat || !location.lng) {
      console.error("❌ Missing coordinates:", location);
      alert("Please select a valid location with coordinates");
      return;
    }

    console.log("📍 Final location for search:", location);

    // ✅ Convert property type names to IDs
    const propertyTypeIds = getPropertyTypeIds(propertyTypes);
    const propertyTypeIdsString = propertyTypeIds.join(",");

    try {
      const params = new URLSearchParams();

      // ✅ Property type IDs
      if (Array.isArray(propertyTypeIds) && propertyTypeIds.length > 0) {
        propertyTypeIds.forEach((id: string) => {
          params.append("q[property_type_ids][]", id);
        });
      }

      // ✅ Coordinates (REQUIRED)
      params.append("q[latitude]", location.lat.toString());
      params.append("q[longitude]", location.lng.toString());

      // ✅ Neighborhood filter - REPLACE THIS BLOCK
const isNeighborhoodSameAsCity =
  location.neighborhood?.toLowerCase().trim() === location.city?.toLowerCase().trim();

if (location.neighborhood && location.neighborhood.trim() !== "" && !isNeighborhoodSameAsCity) {
  params.append("q[neighborhood]", location.neighborhood.trim());
  console.log("✅ Neighborhood filter:", location.neighborhood);
}

      // ✅ City filter 
      if (location.city && location.city.trim() !== "") {
        params.append("q[city]", location.city.trim());
        console.log("✅ City filter:", location.city);
      }

      // ✅ Dynamic radius
  const radius = (location.neighborhood && !isNeighborhoodSameAsCity) ? 3 : location.city ? 10 : 10;
params.append("q[radius]", radius.toString());
console.log("✅ Search radius:", radius, "km");

      // ✅ Price range
      if (priceRange[0] > 0) {
        params.append("q[min_price]", priceRange[0].toString());
      }
      if (priceRange[1] < 5000) {
        params.append("q[max_price]", priceRange[1].toString());
      }

      // ✅ Guest count
      if (guestCount > 1) {
        params.append("q[guest_count]", guestCount.toString());
      }

      // ✅ Dates
      if (startDate) {
        params.append("q[start_date]", startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        params.append("q[end_date]", endDate.toISOString().split('T')[0]);
      }

      params.append("page", "1");

      const searchQuery = params.toString();
      console.log("🔍 FULL SEARCH QUERY:", searchQuery);

      // ✅ Dispatch search
      const action = await dispatch(searchCategory(searchQuery));

      if (action.payload?.success) {
        console.log("✅ Search successful:", action.payload);

        const locationLabel = [
          location.neighborhood,
          location.city,
          location.country
        ]
          .filter(Boolean)
          .join(", ");

        setPlaceData(location);

        dispatch(
          setSearchData({
            ...storedSearchData,
            startDate,
            endDate,
            latitude: location.lat,
            longitude: location.lng,
            locationData: location,
            label: locationLabel,
            searchedResult: action.payload,
            propertyTypeId: propertyTypeIdsString,
            priceMin: priceRange[0],
            priceMax: priceRange[1],
            guestCount,
          })
        );

        console.log("✅ Redux state updated");

        navigate("/listing-stay-map");

        if (onSearchComplete) {
          onSearchComplete();
        }
      } else {
        console.error("❌ Search failed:", action.payload);
        alert("No properties found. Try adjusting your search criteria.");
      }
    } catch (err) {
      console.error("❌ Search error:", err);
      alert("Search failed. Please try again.");
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative flex items-center">
        <Search className="absolute left-4 w-5 h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search for a neighborhood"
          className="w-full pl-12 pr-28 h-14 rounded-full border border-gray-200 shadow-lg text-md md:text-lg focus:ring-0 focus:border-gray-900 outline-none"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="absolute right-2 bg-[#F56948] hover:bg-[#e2583d] text-white rounded-full px-5 py-2 font-medium transition"
        >
          Search
        </button>
      </div>

      {open && (
        <div className="absolute mt-2 w-full bg-white rounded-2xl shadow-xl z-50 p-4">
          <h4 className="font-semibold text-sm text-gray-700 mb-3">
            Popular Neighborhoods in Lima
          </h4>
          <div className="flex flex-wrap">
            {popularNeighborhoods.map((area) => (
              <button
                key={area.neighborhood}
                onClick={() => {
                  console.log("🏙️ Popular neighborhood clicked:", area);
                  setPlaceData(area);
                  setValue(`${area.neighborhood}, ${area.city}, ${area.country}`);
                  setOpen(false);
                }}
                className="flex items-center mb-3 mr-3 rounded-full px-3 py-1.5 text-sm bg-gray-50 hover:bg-gray-100 transition"
              >
                <span className="mr-2">{area.icon}</span>
                <span className="text-gray-700">{area.neighborhood}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationInputSimple;